import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListChecks, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AppShell from "@/components/platform/AppShell";
import { EmptyState, PageTitle, PriorityBadge, StateBadge } from "@/components/platform/Badges";
import { TaskFormDialog } from "@/components/platform/TaskFormDialog";
import { TaskDetailSheet } from "@/components/platform/TaskDetailSheet";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtD } from "@/lib/platform";

const ALL = "__all";
const STATES = ["Draft", "Open", "In Review", "Awaiting Evidence", "Awaiting Approval", "Remediation Required", "Completed", "Closed", "Cancelled"];
const LINKS = [["control", "Control"], ["vendor", "Vendor"], ["incident", "Incident"], ["request", "Request"], ["retention_batch", "Retention Batch"]];

const FilterSelect = ({ value, onChange, placeholder, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-[170px] text-xs bg-white" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function TaskCentre() {
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ tasks: [], status_counts: {} });
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: "", status: ALL, priority: ALL, entity_type: ALL, mine: false, overdue: false });
  const [creating, setCreating] = useState(false);
  const openId = params.get("task");

  const load = useCallback(async () => {
    const p = { q: f.q || undefined, mine: f.mine || undefined, overdue: f.overdue || undefined };
    ["status", "priority", "entity_type"].forEach((k) => { if (f[k] !== ALL) p[k] = f[k]; });
    try {
      const { data: d } = await api.get("/platform/tasks", { params: p });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const openTask = (id) => setParams(id ? { task: id } : {});

  return (
    <AppShell testId="task-centre-page">
      <PageTitle eyebrow="Task engine" title="Task Centre"
                 subtitle="One task list for every module — link work to vendors, incidents, requests, controls and retention batches."
                 actions={auth.can("tasks", "create") && (
                   <Button onClick={() => setCreating(true)} className="bg-[#008080] hover:bg-[#008080]/90" data-testid="task-create-button">
                     <Plus className="w-4 h-4 mr-1" /> New task
                   </Button>
                 )} />

      <div className="flex gap-2 flex-wrap" data-testid="task-status-chips">
        {STATES.filter((s) => data.status_counts?.[s]).map((s) => (
          <button key={s} onClick={() => set("status")(f.status === s ? ALL : s)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-colors ${
                    f.status === s ? "border-[#003366] bg-white shadow-sm" : "border-slate-200 bg-white/60 hover:bg-white"}`}
                  data-testid={`task-status-chip-${s.toLowerCase().replace(/\s+/g, "-")}`}>
            <StateBadge state={s} /> <span className="font-mono font-semibold text-[#003366]">{data.status_counts[s]}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-xl p-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Search tasks" className="h-9 pl-8 w-[220px] text-xs"
                 data-testid="task-search-input" />
        </div>
        <FilterSelect value={f.status} onChange={set("status")} placeholder="All statuses" options={STATES.map((s) => [s, s])} testId="task-filter-status" />
        <FilterSelect value={f.priority} onChange={set("priority")} placeholder="All priorities"
                      options={["Low", "Medium", "High", "Critical"].map((s) => [s, s])} testId="task-filter-priority" />
        <FilterSelect value={f.entity_type} onChange={set("entity_type")} placeholder="Any link" options={LINKS} testId="task-filter-link" />
        <label className="flex items-center gap-2 text-xs text-slate-600 ml-2">
          <Switch checked={f.mine} onCheckedChange={set("mine")} data-testid="task-filter-mine" /> My tasks
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <Switch checked={f.overdue} onCheckedChange={set("overdue")} data-testid="task-filter-overdue" /> Overdue only
        </label>
      </div>

      {!loading && data.tasks.length === 0 ? (
        <EmptyState icon={ListChecks} title="No tasks match" hint="Create a task or relax the filters." testId="task-empty-state" />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="task-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                <th className="px-4 py-3 font-semibold">Ref</th><th className="px-4 py-3 font-semibold">Task</th>
                <th className="px-4 py-3 font-semibold">Owner</th><th className="px-4 py-3 font-semibold">Due</th>
                <th className="px-4 py-3 font-semibold">Priority</th><th className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.tasks.map((t) => (
                <tr key={t.id} onClick={() => openTask(t.id)} className="border-b border-slate-50 hover:bg-slate-50/80 cursor-pointer transition-colors"
                    data-testid={`task-row-${t.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{t.task_ref}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#003366]">{t.title}</div>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {t.links?.map((l) => (
                        <span key={`${l.entity_type}-${l.entity_id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                          {l.type_label}: {l.label}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-700">{t.owner || <span className="text-slate-400">Unassigned</span>}</td>
                  <td className={`px-4 py-3 text-xs font-mono ${t.is_overdue ? "text-red-600 font-semibold" : "text-slate-600"}`}
                      data-testid={`task-due-${t.id}`}>
                    {fmtD(t.due_date)}{t.is_overdue && " · overdue"}
                  </td>
                  <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                  <td className="px-4 py-3"><StateBadge state={t.status} testId={`task-status-${t.id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TaskFormDialog open={creating} onOpenChange={setCreating} onSaved={(t) => { load(); openTask(t.id); }} />
      <TaskDetailSheet taskId={openId} onClose={() => openTask(null)} onChanged={load} />
    </AppShell>
  );
}
